import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { Button } from "@/components/ui/button";
import { Users, ShieldCheck, Loader2, CheckCircle2, CalendarDays } from "lucide-react";

const postToGateway = ({ action, fields }) => {
  const form = document.createElement("form"); form.method = "POST"; form.action = action;
  Object.entries(fields).forEach(([k, v]) => { const i = document.createElement("input"); i.type = "hidden"; i.name = k; i.value = String(v); form.appendChild(i); });
  document.body.appendChild(form); form.submit();
};

export default function PayShare() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/pay/${token}`).then(({ data }) => setD(data)).catch(() => setErr("This payment link is invalid or has expired.")); }, [token]);
  const pay = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/pay/${token}/initiate`); postToGateway(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };
  const paidCount = d?.members.filter((m) => m.status === "Paid").length || 0;
  return (
    <div className="min-h-[80vh] pt-24 pb-16 px-4 flex items-start justify-center" data-testid="pay-share-page">
      <Seo title="Pay your share" description="Pay your share of a Safarshala group trip." noindex />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-3xl border bg-card p-6 sm:p-8 shadow-xl space-y-5">
        {err && <p className="text-center text-muted-foreground">{err}</p>}
        {!d && !err && <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />}
        {d && (
          <>
            <div><p className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Group trip · split payment</p><h1 className="font-heading text-3xl mt-1">{d.trip_name}</h1><p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><CalendarDays className="h-3.5 w-3.5" /> {d.departure_date} · {d.travellers} travellers · organised by {d.organizer}</p></div>
            <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4 text-center">
              <p className="text-sm text-muted-foreground">Hi {d.member.name}, your share</p>
              <p className="font-heading text-5xl text-primary mt-1" data-testid="share-amount">{inr(d.member.amount)}</p>
              {d.member.status === "Paid" ? <p className="mt-2 inline-flex items-center gap-1 text-emerald-600 font-medium" data-testid="share-paid"><CheckCircle2 className="h-4 w-4" /> Paid on {(d.member.paid_at || "").slice(0, 10)} — thank you!</p>
                : d.online_payments ? <Button className="mt-3 w-full h-11 rounded-full btn-shine" onClick={pay} disabled={busy} data-testid="share-pay">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-1" /> Pay {inr(d.member.amount)} securely</>}</Button>
                  : <p className="text-xs text-muted-foreground mt-2">Online payment is unavailable right now — please pay the organiser directly.</p>}
            </div>
            <div>
              <div className="flex items-center justify-between text-sm"><span className="font-medium">Group progress</span><span className="text-muted-foreground">{inr(d.collected)} of {inr(d.total)} · {paidCount}/{d.members.length} paid</span></div>
              <div className="mt-2 h-2 rounded-full bg-muted"><div className="h-2 rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, (d.collected / d.total) * 100)}%` }} /></div>
              <ul className="mt-3 space-y-1 text-sm" data-testid="share-members">{d.members.map((m, i) => <li key={i} className="flex items-center justify-between"><span>{m.name}</span><span className={m.status === "Paid" ? "text-emerald-600" : "text-muted-foreground"}>{m.status}</span></li>)}</ul>
            </div>
            <p className="text-[11px] text-muted-foreground text-center">Booking {d.booking_id} · Secure payment via PayU</p>
          </>
        )}
      </motion.div>
    </div>
  );
}
